/**
 * Card conditions — frontend mirror of backend/src/utils/conditions.ts.
 *
 * The table is a copy of backend/src/config/conditions.json, and conditions.test.ts
 * fails if the two drift. Every screen that names, abbreviates or orders a
 * condition reads this module; the hand-written copies it replaced disagreed with
 * each other and with the backend (DM vs DMG, Sealed first vs last).
 *
 * `skuCode` is what product SKUs carry and is NOT a display label — show `short`.
 */

export interface CanonicalCondition {
  key: string
  display: string
  short: string
  skuCode: string
  rank: number
  aliases: string[]
}

export const CANONICAL_CONDITIONS: readonly CanonicalCondition[] = [
  { key: 'sealed', display: 'Sealed', short: 'SLD', skuCode: 'S', rank: 0, aliases: ['s'] },
  { key: 'near mint', display: 'Near Mint', short: 'NM', skuCode: 'NM', rank: 1, aliases: ['nm'] },
  { key: 'lightly played', display: 'Lightly Played', short: 'LP', skuCode: 'LP', rank: 2, aliases: ['lp'] },
  { key: 'moderately played', display: 'Moderately Played', short: 'MP', skuCode: 'MP', rank: 3, aliases: ['mp'] },
  { key: 'heavily played', display: 'Heavily Played', short: 'HP', skuCode: 'HP', rank: 4, aliases: ['hp'] },
  { key: 'damaged', display: 'Damaged', short: 'DMG', skuCode: 'DM', rank: 5, aliases: ['dmg', 'dm'] },
]

/** Graded single conditions, best first — what condition pickers offer. */
export const STANDARD_CONDITIONS: readonly string[] = CANONICAL_CONDITIONS
  .filter((condition) => condition.key !== 'sealed')
  .map((condition) => condition.display)

/** Rank for a condition the table does not know; sorts after every known one. */
export const UNKNOWN_CONDITION_RANK = 99

const BY_LOOKUP = new Map<string, CanonicalCondition>()
for (const condition of CANONICAL_CONDITIONS) {
  BY_LOOKUP.set(condition.key, condition)
  BY_LOOKUP.set(condition.display.toLowerCase(), condition)
  for (const alias of condition.aliases) BY_LOOKUP.set(alias.toLowerCase(), condition)
}

// Same tags as backend/src/utils/language-suffix.ts.
const LANGUAGE_SUFFIX_PATTERN = /\s*[-–—]\s*(english|japanese|french|german|italian|spanish|portuguese|korean|chinese(?:\s+traditional|\s+simplified)?|thai|indonesian|en|ja|jp)\s*$/i
const LANGUAGE_PAREN_PATTERN = /\s*\((english|japanese|french|german|italian|spanish|portuguese|korean|chinese(?:\s+traditional|\s+simplified)?|thai|indonesian|en|ja|jp)\)\s*$/i

/** `value` with one trailing " - Japanese" and/or " (Japanese)" tag removed (same as the backend's). */
export function stripLanguageSuffix(value: string): string {
  return value.replace(LANGUAGE_SUFFIX_PATTERN, '').replace(LANGUAGE_PAREN_PATTERN, '')
}

/** Exact match on key, display name or alias (case-insensitive, trimmed). */
export function conditionFromInput(input?: string | null): CanonicalCondition | null {
  const normalized = String(input || '').trim().toLowerCase()
  if (!normalized) return null
  return BY_LOOKUP.get(normalized) || null
}

/** Like conditionFromInput, but also accepts a trailing language tag ("Damaged - Japanese"). */
export function resolveConditionForLabel(input?: string | null): CanonicalCondition | null {
  const exact = conditionFromInput(input)
  if (exact) return exact
  const stripped = String(input || '').replace(LANGUAGE_SUFFIX_PATTERN, '').replace(LANGUAGE_PAREN_PATTERN, '').trim()
  return stripped ? conditionFromInput(stripped) : null
}

export function conditionRank(input?: string | null): number {
  return conditionFromInput(input)?.rank ?? UNKNOWN_CONDITION_RANK
}

/**
 * Abbreviation to show ("NM", "DMG", "SLD"). An unknown condition is shown as
 * written rather than guessed.
 */
export function conditionShortLabel(input?: string | null): string {
  return resolveConditionForLabel(input)?.short ?? String(input ?? '').trim()
}

/** Rank keyed by exact display name and short label, for record-style lookups. */
export const CONDITION_RANK_BY_LABEL: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(
    CANONICAL_CONDITIONS.flatMap((condition) => [
      [condition.display, condition.rank],
      [condition.short, condition.rank],
    ]),
  ),
)
